import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Resolve } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { combineLatest, Observable } from "rxjs";
import { filter, first, map, switchMap, tap } from "rxjs/operators";

import { StorageService } from "@hermes/app-core";
import { FeatureFlagFacade } from "@hermes/states/flipper";
import {
    GridCategoryMetas,
    GridFacade,
    GridProduct,
    GridType,
    UrlManagerService,
} from "@hermes/states/grid-filters-state";
import { MenuFacade } from "@hermes/states/menu";

/**
 * This resolver ensure that grid page is accessed only when products are resolved.
 */
@Injectable()
export class CategoryProductsResolver implements Resolve<GridProduct[]> {
    private pimCode = "";

    constructor(
        private gridFacade: GridFacade,
        private menuFacade: MenuFacade,
        private urlManager: UrlManagerService,
        private storageService: StorageService,
        private featureFlagFacade: FeatureFlagFacade,
        private cookieService: CookieService,
    ) {}

    public resolve(route: ActivatedRouteSnapshot): Observable<GridProduct[]> {
        const missingFacet = this.cookieService.get("missingFacet") === "true";

        const gridDataFromUrl = this.urlManager.parseRoute(route);

        return this.gridFacade.category$.pipe(
            tap((category) => {
                {
                    this.gridFacade.fetchGridCategoryMetas(
                        category?.pimCode ?? "",
                    );
                    this.pimCode = category?.pimCode ?? "";
                }
            }),
            switchMap(() => this.gridFacade.pendingCategoryMetas$),
            filter((value) => !value),
            switchMap(() => this.gridFacade.categoryMetas$),
            // Replace undefined with empty object as resolvers need a truthy value
            map((categoryMetas) => categoryMetas || ({} as GridCategoryMetas)),
            switchMap(() =>
                combineLatest([this.menuFacade.isFinalCategory(this.pimCode)]),
            ),
            tap(([isFinalCategory]) => {
                this.gridFacade.initGridPage({
                    displayMode:
                        this.storageService.getSessionStorageItem(
                            "gridDisplayMode",
                        ),
                    gridType: gridDataFromUrl?.facet?.enabled
                        ? GridType.facet
                        : GridType.category,
                    searchTerm: route.queryParams.s,
                    pageNumber:
                        isFinalCategory &&
                        !gridDataFromUrl?.facet?.enabled &&
                        route.queryParams.page
                            ? Number(route.queryParams.page)
                            : 1,
                    ...gridDataFromUrl,
                    facet: {
                        ...gridDataFromUrl?.facet,
                        missingFacet,
                    },
                });
            }),
            switchMap(() => this.gridFacade.products$),
            first(Boolean),
        );
    }
}
